import React, { useEffect, useMemo, useState } from 'react';
import type { Category } from '../../types/api-responses';
import { resolveImageUrl } from '../../utils/imageUrl';
import styles from './CategoryBrowseModal.module.css';

/**
 * CR-2026-05-15 — Browse master categories and subscribe with one click.
 *
 * Side-panel modal opened from My Business → Categories. Lists the active
 * master Category rows, lets the owner search, and exposes a single
 * "+ Subscribe" pill per row. Already-subscribed categories show a green
 * "✓ Added" disabled pill instead.
 *
 * The actual subscribe call lives in the parent (`onSubscribe`) — this
 * component is pure UI + intermediate "subscribing…" state for the row
 * being acted on. The parent owns BE coordination + list refresh.
 */
export interface CategoryBrowseModalProps {
    isOpen: boolean;
    onClose: () => void;
    master: Category[];
    subscribedIds: Set<number>;
    onSubscribe: (categoryId: number) => Promise<void>;
    loading?: boolean;
}

const CategoryBrowseModal: React.FC<CategoryBrowseModalProps> = ({
    isOpen,
    onClose,
    master,
    subscribedIds,
    onSubscribe,
    loading = false,
}) => {
    const [search, setSearch] = useState('');
    const [busyId, setBusyId] = useState<number | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setBusyId(null);
        }
    }, [isOpen]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return master;
        return master.filter(c => c.name.toLowerCase().includes(q));
    }, [master, search]);

    const handleClick = async (id: number) => {
        if (subscribedIds.has(id) || busyId !== null) return;
        setBusyId(id);
        try {
            await onSubscribe(id);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                <div className={styles.header}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={loading || busyId !== null}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <h2 className={styles.title}>Browse categories</h2>
                    <p className={styles.subtitle}>
                        Pick categories that match your salon — customers will find you
                        under each one you add.
                    </p>
                </div>

                <div className={styles.searchWrap}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search categories…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className={styles.list}>
                    {filtered.length === 0 ? (
                        <div className={styles.empty}>
                            {search.trim() ? (
                                <>
                                    No categories match "{search.trim()}".
                                    <br />
                                    Try a different search, or scroll down to request a custom one.
                                </>
                            ) : (
                                <>Loading categories…</>
                            )}
                        </div>
                    ) : (
                        filtered.map(c => {
                            const added = subscribedIds.has(c.id);
                            const busy = busyId === c.id;
                            return (
                                <div key={c.id} className={styles.row}>
                                    <div className={styles.rowThumb}>
                                        {c.imageUrl ? (
                                            <img src={resolveImageUrl(c.imageUrl)} alt="" />
                                        ) : (
                                            <span aria-hidden="true">🏷️</span>
                                        )}
                                    </div>
                                    <div className={styles.rowMain}>
                                        <div className={styles.rowName}>{c.name}</div>
                                        <div className={styles.rowHint}>
                                            {added
                                                ? 'Already in your list'
                                                : "Customers filtering this will see your salon"}
                                        </div>
                                    </div>
                                    <div className={styles.rowAction}>
                                        {added ? (
                                            <span className={styles.addedPill}>✓ Added</span>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.subscribeBtn}
                                                onClick={() => handleClick(c.id)}
                                                disabled={busy || busyId !== null}
                                            >
                                                {busy ? 'Adding…' : '+ Subscribe'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className={styles.footer}>
                    Adding a category is instant — Devanza customers see your salon under it right away.
                </div>
            </div>
        </div>
    );
};

export default CategoryBrowseModal;
